/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    BoolFieldDefinition,
    CheckboxFormGroup,
    ControlType,
    FieldType,
    ISettingGroupDefinition,
    Levels,
    SettingGroupDefinition,
    StringResource,
    UIDefinitions
} from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionPanelCheckboxData,
    IFieldDefinitionPanelCheckboxProps,
    IFieldDefinitionsPanelData,
    IFieldDefinitionsPanelProps
} from "../Panels";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IDomainDataMapper } from "./IDomainDataMapper";

const logger: ILogger = getLogger();

export class DomainDataMapperCheckbox implements IDomainDataMapper {
    /**
     * Field type expected by this mapper
     */
    readonly RequiredFieldType = FieldType.Boolean;

    /**
     * Control type expected by this mapper
     */
    readonly RequiredControlType = ControlType.CheckboxField;

    /**
     * Setting group definition object
     */

    readonly definition: ISettingGroupDefinition;

    constructor(definition: ISettingGroupDefinition) {
        this.definition = definition;
    }

    /**
     * Build panel properties for adding a new control to
     * the definition.
     *
     * @returns Panel definition properties.
     */
    BuildPanelPropsForNewItem = (): IFieldDefinitionsPanelProps => {
        const result: IFieldDefinitionPanelCheckboxProps = {
            common: {
                panelTitle: "Add Checkbox",
                originalSettingName: "",
                isNew: true,
                isOpen: true
            },
            data: {
                definitionType: ControlType.CheckboxField,
                settingName: "",
                title: "",
                tooltip: "",
                helper: "",
                helpTextValid: true,
                defaultValue: false,
                accessLevels: Levels.Classic,
                auditChanges: false,
                visibleRule: "",
                enableRule: ""
            }
        };

        return result;
    };

    /**
     * Build panel properties for an existing control in
     * the setting definition.
     *
     * @param ctrlGuid Control GUID
     * @returns Panel definition properties
     */
    BuildPanelPropsForExistingItem = (ctrlGuid: string): IFieldDefinitionsPanelProps => {
        const sg = new SettingGroupDefinition(this.definition);
        const [cfg, bfd] = this.findControlAndFieldDefinition(sg, ctrlGuid);
        const sr = sg.UI.StringResources;

        const result: IFieldDefinitionPanelCheckboxProps = {
            common: {
                panelTitle: "Edit Checkbox",
                originalSettingName: cfg.SettingName,
                isNew: false,
                isOpen: true
            },
            data: {
                ctrlGuid,
                definitionType: ControlType.CheckboxField,
                settingName: bfd.SettingName,
                title:
                    sr.get(StringResource.CreateMapKey(cfg.LabelTextStringId, "en-US"))?.Text ?? "",
                tooltip:
                    sr.get(StringResource.CreateMapKey(cfg.TooltipStringId, "en-US"))?.Text ?? "",
                helper:
                    sr.get(StringResource.CreateMapKey(cfg.HelperTextStringId, "en-US"))?.Text ??
                    "",
                helpTextValid: true,
                defaultValue: bfd.DefaultValue,
                accessLevels: bfd.AccessLevel,
                auditChanges: bfd.AuditChanges,
                visibleRule: cfg.VisibleRuleName,
                enableRule: cfg.EnableRuleName
            }
        };

        return result;
    };

    /**
     * Saves panel data to the setting definition.
     *
     * @param data Panel data
     * @returns New/updated setting group definition
     */
    SaveDataToDefinition = (data: IFieldDefinitionsPanelData): ISettingGroupDefinition => {
        if (data.definitionType !== ControlType.CheckboxField) {
            logger.logError("Invalid control type", {
                controlType: ControlType[data.definitionType]
            });
            throw new Error("Invalid definition type.");
        }

        const cd = data as IFieldDefinitionPanelCheckboxData;
        const sg = new SettingGroupDefinition(this.definition);

        if (data.ctrlGuid === undefined) {
            this.addNewCheckboxField(cd, sg);
        } else {
            this.updateExistingCheckboxField(cd, sg);
        }
        return sg.toISettingGroupDefinition();
    };

    /**
     * Adds a new checkbox field to the setting definition
     * @param cd Panel checkbox data
     * @param sg Setting group definition
     */
    private addNewCheckboxField(
        cd: IFieldDefinitionPanelCheckboxData,
        sg: SettingGroupDefinition
    ): void {
        const bfd = new BoolFieldDefinition(cd.settingName, cd.defaultValue);
        bfd.AccessLevel = cd.accessLevels;
        bfd.AuditChanges = cd.auditChanges;
        sg.AddFieldDefinition(bfd);

        const cfg = sg.UI.AddCheckboxFormGroup(cd.settingName);
        cfg.EnableRuleName = cd.enableRule;
        cfg.VisibleRuleName = cd.visibleRule;

        this.updateStringResources(sg.UI, cfg, cd.title, cd.tooltip, cd.helper);
    }

    /**
     *
     * @param cd Panel checkbox data
     * @param sg String group definition
     */
    private updateExistingCheckboxField(
        cd: IFieldDefinitionPanelCheckboxData,
        sg: SettingGroupDefinition
    ): void {
        if (cd.ctrlGuid === undefined) {
            logger.logError("Control guid is undefined.");
            throw new Error("Control guid is undefined.");
        }

        const [cfg, sfd] = this.findControlAndFieldDefinition(sg, cd.ctrlGuid);
        sfd.DefaultValue = cd.defaultValue;
        sfd.AccessLevel = cd.accessLevels;
        sfd.AuditChanges = cd.auditChanges;
        cfg.EnableRuleName = cd.enableRule;
        cfg.VisibleRuleName = cd.visibleRule;
        this.updateStringResources(sg.UI, cfg, cd.title, cd.tooltip, cd.helper);
    }

    /**
     * Updates the string resources in the definition.
     * @param sg Setting group definition class
     * @param cfg Checkbox form group
     * @param label Label text
     * @param tooltip Tooltip text
     */
    private updateStringResources(
        ui: UIDefinitions,
        cfg: CheckboxFormGroup,
        label: string,
        tooltip: string,
        helper: string
    ) {
        if (!ui.UpdateStringResourceText(cfg.LabelTextStringId, label)) {
            logger.logError("Error finding label string resource", {
                stringId: cfg.LabelTextStringId
            });
            throw new Error("Error getting label string resource");
        }

        if (!ui.UpdateStringResourceText(cfg.TooltipStringId, tooltip)) {
            logger.logError("Error finding tooltip string resource", {
                stringId: cfg.TooltipStringId
            });
            throw new Error("Error getting tooltip string resource");
        }

        if (!ui.UpdateStringResourceText(cfg.HelperTextStringId, helper)) {
            logger.logError("Error finding helper string resource", {
                stringId: cfg.HelperTextStringId
            });
            throw new Error("Error getting helper string resource");
        }
    }

    /**
     * Find the control and field definition for a
     * control GUID
     * @param sg Setting group definition
     * @param ctrlGuid Control GUID
     * @returns CheckboxFormGroup and BoolFieldDefinition
     * associated with the control
     */
    private findControlAndFieldDefinition = (
        sg: SettingGroupDefinition,
        ctrlGuid: string
    ): [CheckboxFormGroup, BoolFieldDefinition] => {
        const ctrlIndex = sg.UI.Controls.findIndex(i => i.CtrlGuid == ctrlGuid);
        if (ctrlIndex === -1) {
            logger.logError("Unable to find control", { ctrlGuid });
            throw new Error("Unable to find control.");
        }

        const uiCtrl = sg.UI.Controls[ctrlIndex];
        if (uiCtrl.ControlDiscriminator !== this.RequiredControlType) {
            logger.logError("Control type is not checkbox", {
                type: ControlType[uiCtrl.ControlDiscriminator]
            });
            throw new Error("Invalid control type.  Expecting Checkbox control.");
        }

        const cfg = uiCtrl as CheckboxFormGroup;
        const fd = sg.Fields.get(cfg.SettingName);
        if (fd === undefined || fd.FieldDiscriminator !== this.RequiredFieldType) {
            logger.logError("Field type is not string", {
                type: fd?.FieldDiscriminator.toString()
            });
            throw new Error("Invalid field type.");
        }

        return [cfg, fd as BoolFieldDefinition];
    };
}
